import type { QueryStringInput } from '@hello-aria/query-string'
import { mapFilterParams, mapSortParams, type PaginationParamsInput } from '@hello-aria/query-string/dist/stringify'

export type HttpErrorInit = {
    status: number
    code?: string
    message?: string
    details?: unknown
}

/*
 * Named fields, so a message can never land in the `code` slot. An API that sends no message
 * leaves the code as the message.
 */
export class HttpError extends Error {
    public readonly status: number
    public readonly code?: string
    public readonly details?: unknown

    constructor({ status, code, message, details }: HttpErrorInit) {
        super(message || code)
        // Error tracking groups errors by name.
        this.name = 'HttpError'
        this.status = status
        this.code = code
        this.details = details
    }

    toString(): string {
        return this.code?.trim() || super.toString()
    }
}

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null

/*
 * A cancelled fetch rejects with a DOMException named `AbortError`, and axios with a `CanceledError`.
 * The name decides, because the DOMException class differs between realms.
 */
export const isAbortError = (error: unknown): boolean =>
    isRecord(error) && (error.name === 'AbortError' || error.name === 'CanceledError')

const readString = (value: unknown, key: string): string | undefined => {
    if (!isRecord(value)) return undefined
    const field = value[key]
    return typeof field === 'string' ? field : undefined
}

// The fallback the hand-written clients of portal and makalu gave a failure with nothing to show.
const UNEXPECTED_SERVER_ERROR = 'Unexpected server error'

const readDetails = (value: unknown): unknown => (isRecord(value) ? value.details : undefined)

/*
 * The APIs answer `{ code, message }`. An app proxy answers `{ error }` when it fails before the
 * API is reached, and a gateway can nest the pair under `error`. All three reach the user. A
 * gateway that answers an HTML page gives neither, and the user still gets a message. A `details`
 * object, when the API sends one, carries structured context a generic message cannot (a 422's
 * missing fields, for example).
 */
export const toHttpError = (response: Response, body: unknown): HttpError => {
    const nested = isRecord(body) ? body.error : undefined
    const code = readString(body, 'code') ?? readString(nested, 'code')
    const message =
        readString(body, 'message') ??
        readString(nested, 'message') ??
        (typeof nested === 'string' ? nested : undefined)
    const details = readDetails(body) ?? readDetails(nested)

    return new HttpError({
        status: response.status,
        code,
        message: message || code || UNEXPECTED_SERVER_ERROR,
        details
    })
}

type FetchResult = { data?: unknown; error?: unknown; response: Response }

// An operation the document gives no success response resolves to `unknown`, not to `never`.
export type SuccessData<Result> = Result extends { data: infer Data; error?: never }
    ? [Data] extends [never]
        ? unknown
        : Data
    : never

// openapi-fetch sets `data` exactly when the response is ok and `error` stays unset.
const isSuccess = <Result extends FetchResult>(result: Result): result is Result & { data: SuccessData<Result> } =>
    result.error === undefined && result.response.ok

/** Throws an `HttpError` when the response failed, or resolves the body with its response. */
export const unwrapResponse = async <Result extends FetchResult>(
    pending: Promise<Result>
): Promise<{ data: SuccessData<Result>; response: Response }> => {
    const result = await pending

    // A failed response with an empty body carries no `error`, so the status decides too.
    if (!isSuccess(result)) throw toHttpError(result.response, result.error)

    return { data: result.data, response: result.response }
}

/*
 * openapi-fetch resolves `{ data, error }` and never throws. React Query marks a query as failed
 * only when its function throws, so every call goes through this.
 */
export const unwrap = async <Result extends FetchResult>(pending: Promise<Result>): Promise<SuccessData<Result>> => {
    const { data } = await unwrapResponse(pending)

    return data
}

export type Pagination = {
    totalCount?: number
    hasNextPage?: boolean
    hasPrevPage?: boolean
    startCursor?: string
    endCursor?: string
}

/** The envelope `flattenInfiniteData` and `getPageSize` read in the apps. */
export type Page<Item> = {
    body: Item[]
    pagination: Pagination
}

/** The query parameters every list operation declares. */
export type ListQuery = {
    filter?: string[]
    sort?: string
    first?: string
    after?: string
    last?: string
    before?: string
}

/** A `QueryStringInput` whose extra parameters are typed by the operation they are sent to. */
export type ListInput<Params extends object = object> = Omit<QueryStringInput, 'params'> & {
    params?: Params
}

const toPaginationQuery = (pagination: PaginationParamsInput | undefined): ListQuery => {
    if (!pagination) return {}
    if ('last' in pagination) return { last: String(pagination.last), before: pagination.before }

    return { first: String(pagination.first), after: pagination.after }
}

/*
 * The operators of `@hello-aria/query-string` build the values, as they did before the client. The
 * documents type `sort` as one string and the APIs read the first sort only, so only the first is sent.
 */
export const toListQuery = <Params extends object = object>({
    filters,
    sorts,
    pagination,
    params
}: ListInput<Params>): ListQuery & Partial<Params> => {
    const [sort] = sorts ?? []
    const extraParams: Partial<Params> = params ?? {}
    const listQuery: ListQuery = {
        ...toPaginationQuery(pagination),
        filter: filters?.flatMap(mapFilterParams),
        sort: sort ? mapSortParams(sort) : undefined
    }

    return { ...extraParams, ...listQuery }
}
